import React, { useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { CATEGORIES, slugToName } from "../lib/categories";
import ProductCard from "../components/ProductCard";

export default function Shop() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [sort, setSort] = useState("newest");
  const [brand, setBrand] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products", category, q, sort, brand, maxPrice],
    queryFn: async () => {
      const params = { sort, limit: 60 };
      if (category) params.category = category;
      if (q) params.q = q;
      if (brand) params.brand = brand;
      if (maxPrice) params.max_price = parseFloat(maxPrice);
      return (await api.get("/products", { params })).data;
    },
  });

  const brands = useMemo(() => Array.from(new Set(products.map((p) => p.brand))).sort(), [products]);
  const title = category ? slugToName[category] : q ? `Search: "${q}"` : "All Products";

  return (
    <div data-testid="shop-page" className="bg-white">
      {/* Header band */}
      <div className="border-b border-[#0a0a0a] bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="rh-overline mb-3">Nº {CATEGORIES.find((c) => c.slug === category)?.code || "00"} — DEPARTMENT</div>
          <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tighter" data-testid="shop-title">{title}</h1>
          <p className="text-[#4a4a4a] mt-3 text-sm">{products.length} product{products.length !== 1 ? "s" : ""}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-12 gap-8">
        {/* Filters */}
        <aside className="lg:col-span-3 space-y-8" data-testid="filters-sidebar">
          <div>
            <div className="rh-overline mb-4">Categories</div>
            <div className="space-y-1">
              <Link to="/shop" className={`block text-sm py-1 ${!category ? "text-[#002fa7] font-semibold" : ""}`}>All categories</Link>
              {CATEGORIES.map((c) => (
                <Link key={c.slug} to={`/shop/${c.slug}`} className={`block text-sm py-1 ${category === c.slug ? "text-[#002fa7] font-semibold" : "text-[#0a0a0a] hover:text-[#002fa7]"}`}>
                  <span className="font-mono text-[10px] text-[#4a4a4a] mr-2">{c.code}</span>{c.name}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <div className="rh-overline mb-4">Brand</div>
            <select data-testid="brand-filter" value={brand} onChange={(e) => setBrand(e.target.value)} className="w-full border border-[#0a0a0a] px-3 py-2 text-sm bg-white">
              <option value="">All brands</option>
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div>
            <div className="rh-overline mb-4">Max price</div>
            <input data-testid="max-price-filter" type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="e.g. 500" className="w-full border border-[#0a0a0a] px-3 py-2 text-sm" />
          </div>
        </aside>

        {/* Grid */}
        <div className="lg:col-span-9">
          <div className="flex items-center justify-between border-b border-[#e5e5e5] pb-4 mb-6">
            <div className="text-xs text-[#4a4a4a] font-mono">{products.length} RESULTS</div>
            <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-[#0a0a0a] px-3 py-2 text-sm bg-white">
              <option value="newest">Newest</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>

          {isLoading ? (
            <div className="text-center py-20 text-[#4a4a4a]">Loading products…</div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 text-[#4a4a4a]" data-testid="no-results">No products match your filters.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
              {products.map((p) => <ProductCard key={p.product_id} p={p} testIdPrefix="shop" />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
